import React, { useContext, useEffect, useState, useRef } from 'react'
import PageTitle from '../../Components/PageTitle'
import { ConfigContext } from '../../Context/ConfigContext'
import { GlobalLimitChanger, SubmitBtn } from '../../Components/InputElements'
import { ContentLoader, NoRecords, TableRows } from '../../Components/Shimmer'
import axios from 'axios'
import Swal from 'sweetalert2'
import Select from 'react-select'

const HeroSliders = () => {
    const { primaryColor, apiHeaderJson, apiURL, apiHeaderFile, selectTheme, selectStyle } = useContext(ConfigContext)
    const headersJson = apiHeaderJson
    const headersFile = apiHeaderFile

    const [sliderTitle, setSliderTitle] = useState('')
    const [sliderDescription, setSliderDescription] = useState('')
    const [sliderSeq, setSliderSeq] = useState('') // NEW: sequence
    const [selectedCategory, setSelectedCategory] = useState(null)
    const [sliderImage, setSliderImage] = useState(null)
    const [imagePreview, setImagePreview] = useState('')
    const [mobileSliderImage, setMobileSliderImage] = useState(null)
    const [mobileImagePreview, setMobileImagePreview] = useState('')
    const [data, setData] = useState([])
    const [categories, setCategories] = useState([])
    const [categoryOptions, setCategoryOptions] = useState([])
    const [sliderId, setSliderId] = useState('')
    const [next, setNext] = useState(false)
    const [prev, setPrev] = useState(false)
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(10)
    const [totalRecords, setTotalRecords] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [loading, setLoading] = useState(true)
    const [submitLoading, setSubmitLoading] = useState(false)
    const [keyword, setKeyword] = useState('')
    const [errors, setErrors] = useState({})
    const [isDragOver, setIsDragOver] = useState(false)
    const [isMobileDragOver, setIsMobileDragOver] = useState(false)
    const [isInfoLoading, setIsInfoLoading] = useState(false)

    const [showText, setShowText] = useState(0)
    const [link, setLink] = useState('')

    const fileInputRef = useRef(null)
    const mobileFileInputRef = useRef(null)
    const dropZoneRef = useRef(null)
    const mobileDropZoneRef = useRef(null)

    const validateForm = () => {
        const errors = {}

        if (!sliderTitle.trim()) {
            errors.sliderTitle = "Slider title is required"
        }
        if (!sliderDescription.trim()) {
            errors.sliderDescription = "Slider description is required"
        }
        if (!sliderId && !sliderImage) {
            errors.sliderImage = "Slider image is required"
        }
        // Optional sequence validation
        if (sliderSeq && isNaN(sliderSeq)) {
            errors.sliderSeq = "Sequence must be a number"
        }

        setErrors(errors)
        return Object.keys(errors).length === 0
    }

    const validateImageFile = (file) => {
        const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
        if (!validImageTypes.includes(file.type)) {
            return {
                isValid: false,
                message: 'Please select a valid image file (JPEG, JPG, PNG, WEBP only).'
            }
        }
        const maxSize = 1024 * 1024 // 1MB
        if (file.size > maxSize) {
            return {
                isValid: false,
                message: 'Image size must be less than 1MB.'
            }
        }
        return { isValid: true }
    }

    const resetForm = () => {
        setSliderTitle("")
        setSliderDescription("")
        setSliderSeq("") // NEW: reset sequence
        setSelectedCategory(null)
        setSliderImage(null)
        setImagePreview("")
        setMobileSliderImage(null)
        setMobileImagePreview("")
        setErrors({})
        setSliderId('')
        setIsDragOver(false)
        setIsMobileDragOver(false)
        setShowText(0)
        setLink('')
        if (fileInputRef.current) fileInputRef.current.value = ''
        if (mobileFileInputRef.current) mobileFileInputRef.current.value = ''
    }

    // ---- Desktop Image Handlers ----
    const handleDragOver = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(true)
    }

    const handleDragLeave = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(false)
    }

    const handleDrop = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(false)
        const files = e.dataTransfer.files
        if (files && files.length > 0) {
            const file = files[0]
            const validation = validateImageFile(file)
            if (validation.isValid) {
                processImageFile(file)
            } else {
                Swal.fire('Error!', validation.message, 'error')
            }
        }
    }

    const handleFileSelect = (e) => {
        const file = e.target.files[0]
        if (file) {
            const validation = validateImageFile(file)
            if (validation.isValid) {
                processImageFile(file)
            } else {
                Swal.fire('Error!', validation.message, 'error')
                if (fileInputRef.current) fileInputRef.current.value = ''
            }
        }
    }

    const processImageFile = (file) => {
        setSliderImage(file)
        const previewUrl = URL.createObjectURL(file)
        setImagePreview(previewUrl)
        if (errors.sliderImage) {
            setErrors({ ...errors, sliderImage: "" })
        }
    }

    const handleDropZoneClick = () => {
        fileInputRef.current?.click()
    }

    const removeImage = () => {
        setSliderImage(null)
        setImagePreview('')
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    // ---- Mobile Image Handlers ----
    const handleMobileDragOver = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsMobileDragOver(true)
    }

    const handleMobileDragLeave = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsMobileDragOver(false)
    }

    const handleMobileDrop = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsMobileDragOver(false)
        const files = e.dataTransfer.files
        if (files && files.length > 0) {
            const file = files[0]
            const validation = validateImageFile(file)
            if (validation.isValid) {
                processMobileImageFile(file)
            } else {
                Swal.fire('Error!', validation.message, 'error')
            }
        }
    }

    const handleMobileFileSelect = (e) => {
        const file = e.target.files[0]
        if (file) {
            const validation = validateImageFile(file)
            if (validation.isValid) {
                processMobileImageFile(file)
            } else {
                Swal.fire('Error!', validation.message, 'error')
                if (mobileFileInputRef.current) mobileFileInputRef.current.value = ''
            }
        }
    }

    const processMobileImageFile = (file) => {
        setMobileSliderImage(file)
        const previewUrl = URL.createObjectURL(file)
        setMobileImagePreview(previewUrl)
        if (errors.mobileSliderImage) {
            setErrors({ ...errors, mobileSliderImage: "" })
        }
    }

    const handleMobileDropZoneClick = () => {
        mobileFileInputRef.current?.click()
    }

    const removeMobileImage = () => {
        setMobileSliderImage(null)
        setMobileImagePreview('')
        if (mobileFileInputRef.current) mobileFileInputRef.current.value = ''
    }

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes'
        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!validateForm()) return

        if (sliderImage) {
            const validation = validateImageFile(sliderImage)
            if (!validation.isValid) {
                Swal.fire('Error!', validation.message, 'error')
                return
            }
        }
        if (mobileSliderImage) {
            const validation = validateImageFile(mobileSliderImage)
            if (!validation.isValid) {
                Swal.fire('Error!', validation.message, 'error')
                return
            }
        }

        setSubmitLoading(true)
        try {
            const formData = new FormData()
            formData.append('slider_title', sliderTitle)
            formData.append('slider_description', sliderDescription)
            formData.append('slider_seq', sliderSeq ? Number(sliderSeq) : '') // NEW: include sequence
            formData.append('show_text', showText)
            formData.append('link', link || '')

            if (selectedCategory) {
                formData.append('category_id', selectedCategory.value)
            }
            if (sliderImage) {
                formData.append('slider_image', sliderImage)
            }
            if (mobileSliderImage) {
                formData.append('mobile_slider_image', mobileSliderImage)
            }
            if (sliderId) {
                formData.append('slider_id', sliderId)
            }

            const response = await axios.post(`${apiURL}Masters/CreateHomeSlider`, formData, { headers: headersFile })
            const { message, success } = response.data

            if (success) {
                resetForm()
                getData()
                Swal.fire('Success!', message, 'success')
            }
        } catch (error) {
            console.log("error", error)
            Swal.fire('Error!', 'Something went wrong', 'error')
        } finally {
            setSubmitLoading(false)
        }
    }

    const getData = async () => {
        try {
            setLoading(true)
            const response = await axios.get(`${apiURL}Masters/GetHomeSliders`, {
                headers: headersJson,
                params: { keyword, page, limit }
            })
            const { success, data, page: currentPage, next, prev, total_pages, total_records } = response.data

            if (success) {
                setData(data)
                setPage(currentPage)
                setNext(next)
                setPrev(prev)
                setTotalPages(total_pages)
                setTotalRecords(total_records)
            }
        } catch (error) {
            console.log("error", error)
        } finally {
            setLoading(false)
        }
    }

    const getCategories = async () => {
        try {
            const response = await axios.get(`${apiURL}Masters/GetCategoryList`, { headers: headersJson })
            const { success, data } = response.data
            if (success) {
                setCategories(data || [])
                const options = (data || []).map(category => ({
                    value: category.category_id,
                    label: category.category_title
                }))
                setCategoryOptions(options)
            }
        } catch (error) {
            console.log("error", error)
        }
    }

    const getInfo = async (slider_id) => {
        try {
            setIsInfoLoading(true);
            const response = await axios.get(`${apiURL}Masters/GetHomeSliderInfo`, {
                headers: headersJson,
                params: { slider_id: slider_id }
            })
            const { success, data } = response.data

            if (success && data[0]) {
                const slider = data[0]
                setSliderTitle(slider.slider_title)
                setSliderDescription(slider.slider_description)
                setSliderSeq(slider.slider_seq ?? '') // NEW: set sequence
                setSliderId(slider.slider_id)
                setShowText(slider.show_text || 0)
                setLink(slider.link || '')

                if (slider.category_id) {
                    const categoryOption = categoryOptions.find(option => option.value === slider.category_id)
                    setSelectedCategory(categoryOption || null)
                } else {
                    setSelectedCategory(null)
                }

                if (slider.slider_image_url) {
                    setImagePreview(`${slider.slider_image_url}`)
                }
                if (slider.mobile_image_url) {
                    setMobileImagePreview(`${slider.mobile_image_url}`)
                }
                setErrors({})
            }
        } catch (error) {
            console.log("error", error)
        } finally {
            setIsInfoLoading(false);
        }
    }

    const handleDelete = async (slider_id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        })
        if (result.isConfirmed) {
            setLoading(true)
            try {
                const body = { slider_id: slider_id }
                const response = await axios.post(`${apiURL}Masters/DeleteHomeSlider`, body, { headers: headersJson })
                const { message, success } = response.data
                Swal.fire('Deleted!', message, success ? 'success' : 'error')
                if (success) getData()
            } catch (error) {
                console.error(error)
                Swal.fire('Error!', 'Something went wrong', 'error')
            } finally {
                setLoading(false)
            }
        }
    }

    const handlePrev = () => { if (prev) setPage(prev => prev - 1) }
    const handleNext = () => { if (next) setPage(prev => prev + 1) }
    const handleChange = (e) => setPage(parseInt(e.target.value, 10))

    useEffect(() => {
        if (categories.length > 0) {
            const options = categories.map(category => ({
                value: category.category_id,
                label: category.category_title
            }))
            setCategoryOptions(options)
        }
    }, [categories])

    useEffect(() => {
        const delayDebounce = setTimeout(() => getData(), 500)
        return () => clearTimeout(delayDebounce)
    }, [keyword])

    useEffect(() => {
        getData()
        getCategories()
    }, [limit, page])

    useEffect(() => {
        return () => {
            if (imagePreview && imagePreview.startsWith('blob:')) URL.revokeObjectURL(imagePreview)
            if (mobileImagePreview && mobileImagePreview.startsWith('blob:')) URL.revokeObjectURL(mobileImagePreview)
        }
    }, [imagePreview, mobileImagePreview])

    return (
        <>
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <PageTitle title="Manage Hero Sliders" primary="Masters" />
                        {isInfoLoading && <ContentLoader />}
                        <div className="row">
                            <div className="col-md-4">
                                <div className="card">
                                    <div className="card-header" style={{ backgroundColor: primaryColor }}>
                                        <h4 className="card-title mb-0 text-white">
                                            {sliderId ? 'Update Slider' : 'New Slider'}
                                        </h4>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-12 mb-3">
                                                <label htmlFor="sliderTitle">Slider Title:</label>
                                                <input
                                                    type="text"
                                                    id="sliderTitle"
                                                    value={sliderTitle}
                                                    onChange={(e) => {
                                                        setSliderTitle(e.target.value)
                                                        if (errors.sliderTitle) setErrors({ ...errors, sliderTitle: "" })
                                                    }}
                                                    className={`form-control ${errors.sliderTitle ? 'is-invalid' : ''}`}
                                                    placeholder="Enter Slider Title"
                                                />
                                                {errors.sliderTitle && <div className="invalid-feedback">{errors.sliderTitle}</div>}
                                            </div>

                                            <div className="col-md-12 mb-3">
                                                <label htmlFor="sliderDescription">Slider Description:</label>
                                                <textarea
                                                    id="sliderDescription"
                                                    value={sliderDescription}
                                                    onChange={(e) => {
                                                        setSliderDescription(e.target.value)
                                                        if (errors.sliderDescription) setErrors({ ...errors, sliderDescription: "" })
                                                    }}
                                                    className={`form-control ${errors.sliderDescription ? 'is-invalid' : ''}`}
                                                    placeholder="Enter Slider Description"
                                                    rows="3"
                                                />
                                                {errors.sliderDescription && <div className="invalid-feedback">{errors.sliderDescription}</div>}
                                            </div>

                                            {/* <div className="col-md-12 mb-3">
                                                <label htmlFor="categoryId">Category: <span className="text-muted">(Optional)</span></label>
                                                <Select
                                                    id="categoryId"
                                                    value={selectedCategory}
                                                    onChange={setSelectedCategory}
                                                    options={categoryOptions}
                                                    isClearable
                                                    isSearchable
                                                    placeholder="Select Category (Optional)"
                                                    theme={selectTheme}
                                                    styles={selectStyle}
                                                    className="react-select-container"
                                                    classNamePrefix="react-select"
                                                />
                                                <small className="text-muted">Choose a category if this slider should be associated with one</small>
                                            </div> */}

                                            <div className="col-md-12 mb-3">
                                                <label className="form-label">Show Text:</label>
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        id="showTextSwitch"
                                                        checked={showText === 1}
                                                        onChange={(e) => setShowText(e.target.checked ? 1 : 0)}
                                                    />
                                                    <label className="form-check-label" htmlFor="showTextSwitch">
                                                        {showText === 1 ? 'Yes' : 'No'}
                                                    </label>
                                                </div>
                                                <small className="text-muted">Enable to display title & description on the slider</small>
                                            </div>

                                            <div className="col-md-12 mb-3">
                                                <label htmlFor="linkInput">Link (URL): <span className="text-muted">(Optional)</span></label>
                                                <input
                                                    type="text"
                                                    id="linkInput"
                                                    value={link}
                                                    onChange={(e) => setLink(e.target.value)}
                                                    className="form-control"
                                                    placeholder="e.g., /products or https://example.com"
                                                />
                                                <small className="text-muted">Add a URL for the slider call-to-action</small>
                                            </div>

                                            {/* NEW: Sequence input */}
                                            <div className="col-md-12 mb-3">
                                                <label htmlFor="sliderSeq">Sequence: <span className="text-muted">(Optional)</span></label>
                                                <input
                                                    type="number"
                                                    id="sliderSeq"
                                                    value={sliderSeq}
                                                    onChange={(e) => {
                                                        setSliderSeq(e.target.value)
                                                        if (errors.sliderSeq) setErrors({ ...errors, sliderSeq: "" })
                                                    }}
                                                    className={`form-control ${errors.sliderSeq ? 'is-invalid' : ''}`}
                                                    placeholder="Enter sequence (optional)"
                                                />
                                                {errors.sliderSeq && <div className="invalid-feedback">{errors.sliderSeq}</div>}
                                                <small className="text-muted">Determines the display order (lower numbers appear first)</small>
                                            </div>

                                            {/* Desktop Image */}
                                            <div className="col-md-12 mb-3">
                                                <label htmlFor="sliderImage">Slider Image (Desktop): <span className="text-danger">*</span></label>
                                                <input
                                                    type="file"
                                                    id="sliderImage"
                                                    accept="image/jpeg, image/jpg, image/png, image/webp"
                                                    onChange={handleFileSelect}
                                                    className="d-none"
                                                    ref={fileInputRef}
                                                />
                                                <div
                                                    ref={dropZoneRef}
                                                    className={`border-2 border-dashed rounded p-4 text-center cursor-pointer ${isDragOver ? 'border-primary bg-light' : 'border-gray-300'} ${errors.sliderImage ? 'border-danger' : ''}`}
                                                    onDragOver={handleDragOver}
                                                    onDragLeave={handleDragLeave}
                                                    onDrop={handleDrop}
                                                    onClick={handleDropZoneClick}
                                                    style={{ borderStyle: isDragOver ? 'solid' : 'dashed', transition: 'all 0.3s ease' }}
                                                >
                                                    {imagePreview ? (
                                                        <div className="position-relative">
                                                            <img src={imagePreview} alt="Desktop Preview" className="img-thumbnail mx-auto" style={{ maxWidth: '200px', maxHeight: '200px', display: 'block' }} />
                                                            <div className="mt-2 small text-muted">{sliderImage && `Size: ${formatFileSize(sliderImage.size)}`}</div>
                                                            <button type="button" className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2" onClick={(e) => { e.stopPropagation(); removeImage() }} style={{ zIndex: 10 }}><i className="ri-close-line"></i></button>
                                                        </div>
                                                    ) : (
                                                        <div>
                                                            <i className="ri-upload-cloud-2-line display-4 text-muted mb-2" style={{ fontSize: '3rem' }}></i>
                                                            <p className="mb-1"><span className="text-primary">Click to upload</span> or drag and drop</p>
                                                            <p className="text-muted small mb-0">PNG, JPG, JPEG, WEBP (Max. 1MB)</p>
                                                        </div>
                                                    )}
                                                </div>
                                                {errors.sliderImage && <div className="invalid-feedback d-block">{errors.sliderImage}</div>}
                                                <small className="text-muted">{sliderId ? 'Select new image to update' : 'Required for new sliders'}</small>
                                            </div>

                                            {/* Mobile Image */}
                                            <div className="col-md-12 mb-3">
                                                <label htmlFor="mobileSliderImage">Slider Image (Mobile): <span className="text-muted">(Optional)</span></label>
                                                <input
                                                    type="file"
                                                    id="mobileSliderImage"
                                                    accept="image/jpeg, image/jpg, image/png, image/webp"
                                                    onChange={handleMobileFileSelect}
                                                    className="d-none"
                                                    ref={mobileFileInputRef}
                                                />
                                                <div
                                                    ref={mobileDropZoneRef}
                                                    className={`border-2 border-dashed rounded p-4 text-center cursor-pointer ${isMobileDragOver ? 'border-primary bg-light' : 'border-gray-300'}`}
                                                    onDragOver={handleMobileDragOver}
                                                    onDragLeave={handleMobileDragLeave}
                                                    onDrop={handleMobileDrop}
                                                    onClick={handleMobileDropZoneClick}
                                                    style={{ borderStyle: isMobileDragOver ? 'solid' : 'dashed', transition: 'all 0.3s ease' }}
                                                >
                                                    {mobileImagePreview ? (
                                                        <div className="position-relative">
                                                            <img src={mobileImagePreview} alt="Mobile Preview" className="img-thumbnail mx-auto" style={{ maxWidth: '200px', maxHeight: '200px', display: 'block' }} />
                                                            <div className="mt-2 small text-muted">{mobileSliderImage && `Size: ${formatFileSize(mobileSliderImage.size)}`}</div>
                                                            <button type="button" className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2" onClick={(e) => { e.stopPropagation(); removeMobileImage() }} style={{ zIndex: 10 }}><i className="ri-close-line"></i></button>
                                                        </div>
                                                    ) : (
                                                        <div>
                                                            <i className="ri-upload-cloud-2-line display-4 text-muted mb-2" style={{ fontSize: '3rem' }}></i>
                                                            <p className="mb-1"><span className="text-primary">Click to upload</span> or drag and drop</p>
                                                            <p className="text-muted small mb-0">PNG, JPG, JPEG, WEBP (Max. 1MB)</p>
                                                        </div>
                                                    )}
                                                </div>
                                                <small className="text-muted">Optional mobile-specific image. If not provided, desktop image may be used.</small>
                                            </div>

                                            <div className="col-md-12 mb-3">
                                                <div className='col-md-12 d-flex align-items-end justify-content-end gap-3 mt-3'>
                                                    {sliderId ? (
                                                        <>
                                                            <button type="button" className="btn btn-light btn-label right me-auto" onClick={resetForm}>
                                                                Reset <i className="ri-arrow-right-line label-icon align-middle fs-16" />
                                                            </button>
                                                            <SubmitBtn icon="ri-save-line" text="Update" onClick={handleSubmit} type="primary" />
                                                        </>
                                                    ) : (
                                                        <SubmitBtn disabled={submitLoading} text={submitLoading ? 'Saving..' : 'Save'} type="primary" onClick={handleSubmit} icon="ri-save-line" />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-8">
                                <div className="card">
                                    <div className="card-header" style={{ backgroundColor: primaryColor }}>
                                        <div className="row d-flex align-items-center">
                                            <div className="col-md-7">
                                                <h4 className="card-title mb-0 text-white">Hero Sliders List</h4>
                                            </div>
                                            <div className="col-md-5">
                                                <div className="position-relative">
                                                    <input type="text" className="form-control pe-4" placeholder="Search sliders..." value={keyword} onChange={(e) => setKeyword(e.target.value)} />
                                                    <span className="position-absolute end-0 top-50 translate-middle-y me-3"><i className="ri-search-line"></i></span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <div className="table-responsive table-card">
                                            <table className="table table-bordered table-striped table-nowrap table-hover mb-0">
                                                {loading ? (
                                                    <TableRows rows="10" colspan="10" /> // updated colspan to 10
                                                ) : (
                                                    <>
                                                        <thead className="table-light text-center">
                                                            <tr>
                                                                <th>#</th>
                                                                <th>Seq</th> {/* NEW: Sequence column */}
                                                                <th>Desktop Image</th>
                                                                <th>Mobile Image</th>
                                                                <th>Title</th>
                                                                <th>Description</th>
                                                                <th>Show Text</th>
                                                                <th>Link</th>
                                                                <th>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {data.length > 0 ? (
                                                                data.map((row) => (
                                                                    <tr key={row.slider_id} className="text-center">
                                                                        <td>{row.slider_id}</td>
                                                                        <td>{row.slider_seq ?? '-'}</td> {/* NEW: show sequence */}
                                                                        <td>
                                                                            {row.slider_image_url ? (
                                                                                <img src={row.slider_image_url} alt={row.slider_title} className="img-thumbnail" style={{ width: '80px', height: '80px', objectFit: 'cover' }} />
                                                                            ) : (
                                                                                <span className="text-muted">No image</span>
                                                                            )}
                                                                        </td>
                                                                        <td>
                                                                            {row.mobile_image_url ? (
                                                                                <img src={row.mobile_image_url} alt={row.slider_title} className="img-thumbnail" style={{ width: '80px', height: '80px', objectFit: 'cover' }} />
                                                                            ) : (
                                                                                <span className="text-muted">—</span>
                                                                            )}
                                                                        </td>
                                                                        <td>{row.slider_title}</td>
                                                                        <td>{row.slider_description}</td>
                                                                        <td>
                                                                            <span className={`badge ${row.show_text === 1 ? 'bg-success' : 'bg-danger'}`}>
                                                                                {row.show_text === 1 ? 'Yes' : 'No'}
                                                                            </span>
                                                                        </td>
                                                                        <td>
                                                                            {row.link ? (
                                                                                <a href={row.link} target="_blank" rel="noopener noreferrer" className="text-primary"><i className="ri-link"></i> Link</a>
                                                                            ) : (
                                                                                <span className="text-muted">—</span>
                                                                            )}
                                                                        </td>
                                                                        <td>
                                                                            <button className="btn btn-sm btn-soft-primary me-2" onClick={() => getInfo(row.slider_id)}><i className='ri-pencil-line'></i></button>
                                                                            <button className="btn btn-sm btn-soft-danger" onClick={() => handleDelete(row.slider_id)}><i className='ri-delete-bin-line'></i></button>
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            ) : (
                                                                <tr><td colSpan={10}><NoRecords /></td></tr> // updated colSpan to 10
                                                            )}
                                                        </tbody>
                                                        <tfoot className='table-light'>
                                                            <tr>
                                                                <th colSpan={10}> {/* updated colSpan to 10 */}
                                                                    <div className="d-flex justify-content-between">
                                                                        <button disabled={!prev && !loading} onClick={handlePrev} className="btn btn-warning btn-label waves-effect waves-light">
                                                                            <i className="ri-arrow-left-line label-icon align-middle fs-16 me-2" /> Previous
                                                                        </button>
                                                                        <div className='col-md-4 d-flex align-items-center'>
                                                                            <small>Total Records: {totalRecords} | Total Pages: {totalPages} | Current Page: {page}</small>
                                                                        </div>
                                                                        <div className='col-md-2'>
                                                                            <select className="form-select" value={page} onChange={handleChange}>
                                                                                {Array.from({ length: totalPages }, (_, i) => (
                                                                                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                                                                                ))}
                                                                            </select>
                                                                        </div>
                                                                        <div className='col-md-2'>
                                                                            <GlobalLimitChanger placeholder="Set limit:" name="globalLimit" value={limit} onChange={setLimit} showAllValue={totalRecords} />
                                                                        </div>
                                                                        <button disabled={!next && !loading} onClick={handleNext} className="btn btn-primary btn-label waves-effect right waves-light">
                                                                            <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2" /> Next
                                                                        </button>
                                                                    </div>
                                                                </th>
                                                            </tr>
                                                        </tfoot>
                                                    </>
                                                )}
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default HeroSliders